import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { storage } from '../../api/client';
import {
  PartnerCard, ServiceCard, ProductCard, JobCard, VehicleCard,
} from '../../components/Cards';
import {
  Chip, Empty, Loading, SectionHeader, Input,
} from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useCart } from '../../context/CartContext';

const RECENT_KEY = 'kw_recent_searches';
const SUGGESTIONS = ['Electrician', 'AC service', 'Salon', 'Grocery', 'Plumber', 'Cab', 'Jobs', 'Gym'];

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'partners', label: 'Shops' },
  { key: 'services', label: 'Services' },
  { key: 'products', label: 'Products' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'vehicles', label: 'Vehicles' },
];

/** Global search across shops, services, products, jobs and vehicles. */
const SearchPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { addItem, quantityOf } = useCart();

  const [query, setQuery] = useState(params.get('q') || '');
  const [type, setType] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    const saved = storage.get(RECENT_KEY);
    if (saved) {
      try { setRecent(JSON.parse(saved)); } catch (e) { setRecent([]); }
    }
  }, []);

  // Keep the URL in step so a search can be shared or reloaded.
  useEffect(() => {
    setParams(query ? { q: query } : {}, { replace: true });
  }, [query, setParams]);

  useEffect(() => {
    if (query.trim().length < 2) { setResults(null); return undefined; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.app.search({ q: query.trim(), type: type || undefined });
        setResults(res.data);
      } catch (e) {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [query, type]);

  const remember = (term) => {
    const next = [term, ...recent.filter((r) => r !== term)].slice(0, 8);
    setRecent(next);
    storage.set(RECENT_KEY, JSON.stringify(next));
  };

  const pick = (term) => { setQuery(term); remember(term); };

  const total = results
    ? Object.values(results).reduce((n, arr) => n + ((arr && arr.length) || 0), 0)
    : 0;

  return (
    <div>
      <form
        onSubmit={(e) => { e.preventDefault(); if (query.trim()) remember(query.trim()); }}
        className="card sh-sm"
        style={{ marginBottom: 8 }}
      >
        <Input
          icon="🔍"
          autoFocus
          placeholder="Search services, shops, products, jobs…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <div className="row no-bar" style={{ gap: 8, overflowX: 'auto' }}>
          {FILTERS.map((f) => (
            <Chip key={f.key || 'all'} label={f.label} active={type === f.key} onClick={() => setType(f.key)} />
          ))}
        </div>
      </form>

      {!query ? (
        <div style={{ paddingTop: 12 }}>
          {recent.length ? (
            <>
              <div className="row-between">
                <h3 className="t-title">Recent searches</h3>
                <button
                  type="button"
                  className="link-btn"
                  style={{ color: 'var(--danger)' }}
                  onClick={() => { setRecent([]); storage.remove(RECENT_KEY); }}
                >
                  Clear
                </button>
              </div>
              <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
                {recent.map((r) => <Chip key={r} label={r} icon="🕘" onClick={() => pick(r)} />)}
              </div>
            </>
          ) : null}

          <h3 className="t-title" style={{ marginTop: 26, marginBottom: 12 }}>Popular on KAAMWALA</h3>
          <div className="row wrap" style={{ gap: 8 }}>
            {SUGGESTIONS.map((s) => <Chip key={s} label={s} onClick={() => pick(s)} />)}
          </div>
        </div>
      ) : loading ? (
        <Loading text="Searching…" />
      ) : !results || total === 0 ? (
        <Empty
          emoji="🔍"
          title={`No results for "${query}"`}
          text="Try a different word or browse all categories."
          actionLabel="Browse categories"
          onAction={() => navigate('/services')}
        />
      ) : (
        <>
          {results.categories && results.categories.length ? (
            <>
              <SectionHeader title="Categories" />
              <div className="row wrap" style={{ gap: 8 }}>
                {results.categories.map((c) => (
                  <Chip
                    key={c._id}
                    label={c.name}
                    icon={c.icon}
                    onClick={() => navigate(`/category/${c._id}`, { state: { category: c } })}
                  />
                ))}
              </div>
            </>
          ) : null}

          {results.partners && results.partners.length ? (
            <>
              <SectionHeader title="Shops & providers" />
              <div className="grid grid-cards">
                {results.partners.map((p, i) => (
                  <Reveal key={p._id} delay={stagger(i, 45)}>
                    <PartnerCard partner={p} onClick={() => navigate(`/business/${p._id}`)} />
                  </Reveal>
                ))}
              </div>
            </>
          ) : null}

          {results.services && results.services.length ? (
            <>
              <SectionHeader title="Services" />
              <div className="grid grid-list">
                {results.services.map((s, i) => (
                  <Reveal key={s._id} delay={stagger(i, 45)}>
                    <ServiceCard service={s} onClick={() => navigate(`/service/${s._id}`)} />
                  </Reveal>
                ))}
              </div>
            </>
          ) : null}

          {results.products && results.products.length ? (
            <>
              <SectionHeader title="Products" />
              <div className="grid grid-products">
                {results.products.map((p) => (
                  <ProductCard
                    key={p._id}
                    product={p}
                    inCart={quantityOf(p._id)}
                    onAdd={() => addItem(p._id, 1).catch(() => {})}
                    onClick={() => navigate(`/product/${p._id}`)}
                  />
                ))}
              </div>
            </>
          ) : null}

          {results.jobs && results.jobs.length ? (
            <>
              <SectionHeader title="Jobs" />
              <div className="grid grid-cards">
                {results.jobs.map((j) => (
                  <JobCard key={j._id} job={j} onClick={() => navigate(`/jobs/${j._id}`)} />
                ))}
              </div>
            </>
          ) : null}

          {results.vehicles && results.vehicles.length ? (
            <>
              <SectionHeader title="Vehicles" />
              <div className="grid grid-products">
                {results.vehicles.map((v) => (
                  <VehicleCard key={v._id} vehicle={v} onClick={() => navigate(`/vehicles/${v._id}`)} />
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default SearchPage;
